import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { HttpService } from '../http.service';
import { Observable, map } from 'rxjs';

/** One catalogue product, resolved from a barcode by `lookupBarcodes`. */
export interface BarcodeMatch {
  /** The barcode as the caller sent it, so a UI can point at the row it came from. */
  barcode: string;
  /**
   * `product_variants.id` is a bigint, which node-pg serialises as a STRING ("7720"). The
   * server coerces it back to a number before it leaves, and `lookupBarcodes` coerces again
   * on arrival, so consumers can compare it with `===` against a form control's id.
   */
  product_variant_id: number;
  /** The catalogue's own spelling of the barcode — what should be stored on the line. */
  catalogue_barcode: string;
  sku: string | null;
  model_number: string | null;
  product_name: string | null;
  /**
   * VAT-INCLUSIVE per unit, the same basis the picker's suggestion price uses.
   *
   * NULL when the catalogue holds no price for this product. Distinct from 0, which means
   * the product really is free — a caller must NOT fall back to 0, or entering the barcode
   * of a price-less product silently rewrites the operator's own price as AED 0.00.
   */
  selling_price: number | null;
  cost_price: number | null;
  quantity_available: number;
  /** Parent product publish status: 'draft' | 'live' | 'hidden'. */
  product_status: string | null;
  product_type: string | null;
}

/**
 * Exhaustive: every barcode asked about comes back in exactly one bucket, so a caller can
 * report per line instead of silently dropping the ones that did not resolve.
 */
export interface BarcodeLookupResult {
  /** Resolved to exactly one live-or-unpublished product. The ONLY bucket a line may link to. */
  found: BarcodeMatch[];
  /** No catalogue product carries this barcode. */
  not_found: string[];
  /** Several variants collapse onto it once case is ignored — the server refuses to guess. */
  ambiguous: string[];
  /**
   * The variant exists but its parent product has been deleted from the catalogue.
   *
   * Its own bucket, not `not_found`: the two need different things from the operator —
   * "check the barcode" versus "this product was withdrawn". And never a `found` entry
   * with a flag, because everything that reads `found` links the line.
   */
  deleted: string[];
}

/**
 * Admin Quotation Requests service (Finance → Quotation Requests).
 * Backend base: /api/v1/quotation-request
 */
@Injectable({
  providedIn: 'root',
})
export class QuotationService extends HttpService<any, any> {
  constructor(httpClient: HttpClient, @Inject('API_URL') apiUrl: string, @Inject('API_VERSION') apiVersion: string) {
    super(httpClient, apiUrl, apiVersion, 'quotation-request');
  }

  // Cache-buster so the browser never serves a stale (304/heuristically cached)
  // response — the admin always fetches the latest data without a manual refresh.
  private bust(): number {
    return Date.now();
  }

  /** List requests (search matches id/name/company/email/phone; optional status). */
  listRequests(page = 1, limit = 10, search = '', status = ''): Observable<any> {
    const params: any = { page, limit, _ts: this.bust() };
    if (search) params.search = search;
    if (status) params.status = status;
    return this.getBy('', params);
  }

  /** KPI cards (counts + %change + money breakdown). Reflects the search filter. */
  getKpis(search = ''): Observable<any> {
    const params: any = { _ts: this.bust() };
    if (search) params.search = search;
    return this.getBy('kpis', params);
  }

  /** A request with all its quotations + line items. */
  getRequest(id: number | string): Observable<any> {
    return this.getBy(`${id}`, { _ts: this.bust() });
  }

  /** All quotations issued against a request. */
  listQuotationsForRequest(id: number | string): Observable<any> {
    return this.getBy(`${id}/quotations`, { _ts: this.bust() });
  }

  /** A single quotation with items. */
  getQuotation(id: number | string): Observable<any> {
    return this.getBy(`quotation/${id}`, { _ts: this.bust() });
  }

  /**
   * Resolve a batch of barcodes to catalogue products for the bulk quotation form.
   *
   * NOT the product search (`POST /product-variant/search`). That search is served by
   * Elasticsearch, and an UNPUBLISHED product does not stay in that index: a product is
   * created as a draft, and the per-variant reindex — reached from an ordinary product
   * edit, an order, or a deal change — treats a non-live product as deleted and drops its
   * document. From then on the picker cannot find it by any term. This endpoint reads
   * Postgres directly with no publish-status filter, so a barcode resolves as soon as the
   * product exists, whatever the index happens to hold. Admin-only for that reason.
   *
   * Answers exhaustively: `{ found, not_found, ambiguous, deleted }`, every barcode asked
   * about in exactly one bucket, echoed in the caller's own spelling.
   */
  lookupBarcodes(barcodes: string[]): Observable<BarcodeLookupResult> {
    // Unwrapped here rather than in the component: `create` hands back the whole JSend
    // envelope, and every caller of this wants the payload. The `??` guards a malformed
    // envelope so a caller iterating the four buckets cannot crash on undefined.
    return this.create({ barcodes }, 'lookup-barcodes').pipe(
      map((res: any) => ({
        // `Number(...)` because product_variant_id is a Postgres bigint: node-pg serialises
        // those as strings, so the wire value can be "7720". The server coerces too, but
        // this is the boundary where the payload becomes a typed BarcodeMatch, and a string
        // slipping past here fails `===` against a form control's numeric id — a link that
        // looks correct in the payload and silently never happens.
        found: (res?.data?.found ?? []).map((f: any) => ({ ...f, product_variant_id: Number(f?.product_variant_id) })),
        not_found: res?.data?.not_found ?? [],
        ambiguous: res?.data?.ambiguous ?? [],
        // Defaulted, so a component switching on the bucket cannot crash against an older
        // server that does not send it.
        deleted: res?.data?.deleted ?? [],
      })),
    );
  }

  /** Create a quotation (manual, or from a request when quotation_request_id is set). */
  createQuotation(payload: any): Observable<any> {
    return this.create(payload, 'quotation');
  }

  /** Edit a quotation (recomputes totals server-side). */
  updateQuotation(id: number | string, payload: any): Observable<any> {
    return this.update(payload, 'quotation', id);
  }

  /** Set a request status (completed / failed / ...). */
  updateRequestStatus(id: number | string, status: string): Observable<any> {
    return this.update({ status }, `${id}`, 'status');
  }

  /** Accept / reject a quotation. */
  updateQuotationStatus(id: number | string, status: string): Observable<any> {
    return this.update({ status }, `quotation/${id}`, 'status');
  }

  /** Convert an accepted quotation into an order. */
  convertToOrder(id: number | string, payload: any): Observable<any> {
    return this.create(payload, `quotation/${id}/convert-to-order`);
  }

  /** Trigger PDF (re)generation; returns { pdf_url } when available. */
  generatePdf(id: number | string): Observable<any> {
    return this.create({}, `quotation/${id}/generate-pdf`);
  }

  /** Fetch the stored PDF URL (generates on demand if missing). */
  getPdf(id: number | string): Observable<any> {
    return this.getBy(`quotation/${id}/pdf`);
  }
}
